<template>
  <div class="page-content-container pivot-table-container">
    <div class="filters-container filter-section d-flex flex-column align-content-center">
      <div class="d-flex flex-row justify-content-between gap-3 mb-3">
        <v-select style="width: 100%;" class="user-selection" v-model="selectedUsers" :options="userOptionsForDropdown" label="displayName" placeholder="Search and select users..." :reduce="user => user.id" @input="checkSelectedUsersLength" multiple></v-select>
        <button @click="handleUserSelection" :disabled="loading || selectedUsers.length === 0" class="filter-btn pt-0 pb-0">
          <i class="fa fa-search"></i>
          Search
        </button>
        <button @click="exportUsersContentReport()" :disabled="loading || exportLoading" class="filter-btn pt-0 pb-0">
          <i class="fas fa-file-csv me-2"></i>
          {{ exportLoading ? "Exporting": "Export" }}
        </button>
        </div>

      <div class="legend">
        <div v-for="(color, type) in contentColors" :key="type" class="legend-item" @click="toggleContentType(type)"
             :class="{ 'legend-item-inactive': hiddenTypes.includes(type) }">
          <span :style="{ backgroundColor: color }" class="legend-color"></span> {{ type }}
        </div>
      </div>

      <div class="legend">
        <div v-for="status in userStatuses" :key="status" class="legend-item" @click="toggleUserStatus(status)"
             :class="{ 'legend-item-inactive': !activeUserStatuses.includes(status) }">
          <span :class="['legend-color', `${status.toLowerCase()}-dot`]"></span>{{ status.charAt(0).toUpperCase() + status.slice(1) }}
        </div>
      </div>

    </div>




    <div class="table-wrapper">
      <div class="loader-overlay" v-if="loading">
        <b-spinner variant="border" label="Loading..."></b-spinner>
      </div>

      <table class="table-container" v-if="!loading && filteredUsers.length && filteredModuleStructure.length">
        <thead>
          <tr>
            <th rowspan="3" class="fixed-column text-center">Students</th>
            <th v-for="module in filteredModuleStructure" :key="module.id" :colspan="getTotalColspan(module)"
              class="module-header" v-b-tooltip.hover.bottom="module.title">
              {{ module.title }}
            </th>
          </tr>
          <tr>
            <template v-for="module in filteredModuleStructure">
              <th v-for="chapter in module.chapters" :key="`${module.id}-${chapter.id}`"
                :colspan="chapter.contents.length" class="chapter-header" v-b-tooltip.hover.bottom="chapter.title">
                {{ truncateText(chapter.title,100) }}
              </th>
            </template>
          </tr>
          <tr class="content-header">
            <template v-for="module in filteredModuleStructure">
              <template v-for="chapter in module.chapters">
                <th v-for="content in chapter.contents" :key="`${module.id}-${chapter.id}-${content.id}`"
                  :style="{ borderTop: `2px solid ${contentColors[content.type] || '#f0f0f0'}` }" v-b-tooltip.hover.righttop="content.title">
                  {{ truncateText(content.title) }}
                </th>
              </template>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in loadedUsers" :key="user.id">
            <td class="fixed-column text-bold text-start p-2">
              {{ user.id }} - {{ user.firstName }} {{ user.lastName }} - ({{getViewedContentCountForUser(user.id)}}/{{ totalContentCount }})
              <p class="text-secondary user-email-phone">{{ user.email }}</p>
              <p class="text-secondary user-email-phone" style="line-height: 0;">{{ user.phone }}
                <span class="status-dot" v-b-tooltip.hover.righttop.html="generateUserInformationTooltip(user)"
                  :class="`${user.enrollmentStatus.toLowerCase()}-dot`">
                </span>
              </p>
            </td>
            <template v-for="module in filteredModuleStructure">
              <template v-for="chapter in module.chapters">
                <td style="font-size: 1rem;" v-for="content in chapter.contents"
                  :key="`${user.id}-${module.id}-${chapter.id}-${content.id}`">
                  <span class="text-success cross-tick-icon" v-if="getContentViewCount(user.id, content.id) > 0">✔</span>
                  <span class="text-danger cross-tick-icon" v-else >✖</span>
                </td>
              </template>
            </template>
          </tr>
        </tbody>
      </table>
      <p v-if="!loading && !isLoadingMore && (!filteredUsers.length || !filteredModuleStructure.length)" class="no-records">
        No Records Found
      </p>
      <div v-if="error" class="error">{{ error }}</div>
      <div class="d-flex flex-row justify-content-center">
        <button v-if="isMoreUsersAvailableToLoad && !isLoadingMore && !loading" @click="loadMoreUsers()" class="filter-user-type-btn">
          Load More
        </button>
      </div>
      <div v-if="isLoadingMore && !loading" class="text-center m-4">
        <b-spinner></b-spinner>
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { toShortDateString } from "../../../helpers/FormatHelpers";
export default {
  name: 'StudentContentViews',
  data() {
    return {
      users: [],
      contents: [],
      userContentViewMap: new Map(),
      loading: true,
      error: null,
      loadedUsers:[],
      numOfUsersToLoad:50,
      currentIndex: 0,
      isLoadingMore:false,
      selectedUsers:[],
      contentColors: {
        StudyMaterial: "#AA336A", 
        FreeText: "#ac33ff",       
        RecordedVideos: "#3333ff", 
        LiveClass: "#7abef3",      
        Assignment: "#fffc33",    
        Flashcards: "#33fff6",   
        Exam: "#117a65",           
        Quiz: "#343A40"            
      },
      hiddenTypes: [],
      userStatuses: ['allowed', 'unenrolled', 'blocked'],
      activeUserStatuses: ['allowed'],
      exportLoading:false
    };
  },
  props: { productVariantData: Object },
  computed: {
    ...mapState('user', ['usersByProductVariant']),
    moduleStructure() {
      if (!this.contents || !this.contents.length) return [];
      
      return this.contents.map(module => {
        if (!module.data || !module.data.children) return null;
        
        const filteredChapters = module.data.children
          .filter(chapter => chapter && chapter.data && chapter.data.children)
          .map(chapter => ({
            id: chapter.id,
            title: chapter.title,
            contents: chapter.data.children.filter(content => content)  
          }))
          .filter(chapter => chapter.contents.length > 0); 

        if (filteredChapters.length > 0) {
          return {
            id: module.id,
            title: module.title,
            chapters: filteredChapters
          };
        }

        return null;
      }).filter(module => module !== null); 
    },
    filteredModuleStructure() {
      return this.moduleStructure.map(module => ({
        ...module,
        chapters: module.chapters.map(chapter => ({
          ...chapter,
          contents: chapter.contents.filter(content => !this.hiddenTypes.includes(content.type))
        })).filter(chapter => chapter.contents.length > 0)
      })).filter(module => module.chapters.length > 0);
    },
    filteredUsers() {
      return this.users.filter(u => {
        const isAnyUserFilteredInDropdown = this.selectedUsers && this.selectedUsers.length > 0;
        const isUserSelectedInDropdown = !isAnyUserFilteredInDropdown || this.selectedUsers.indexOf(u.id) > -1;
        const isFoundInActiveEnrollmentStatuses = this.activeUserStatuses?.indexOf(u.enrollmentStatus.toLowerCase()) > -1;

        return (!isAnyUserFilteredInDropdown || isUserSelectedInDropdown) && isFoundInActiveEnrollmentStatuses;
      });
    },
    totalContentCount() {
      return this.filteredModuleStructure.reduce((total, module) => {
        return total + module.chapters.reduce((chapterTotal, chapter) => {
          return chapterTotal + chapter.contents.length;
        }, 0);
      }, 0);
    },
    userOptionsForDropdown() {
      return this.users.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        displayName: `${user.id} - ${user.firstName} ${user.lastName} - ${user.email} (${user.phone})`
      }));
    },
    isMoreUsersAvailableToLoad(){
      return this.filteredUsers.length > 0 && this.loadedUsers.length < this.filteredUsers.length;
    },
    visibleContentIds(){
      const viewableContentIdsSet = new Set();
      if(this.filteredModuleStructure){
        this.filteredModuleStructure.forEach(module => {
          module.chapters.forEach(chapter => {
            chapter.contents.forEach(content => {
              viewableContentIdsSet.add(parseInt(content.id));
            });
          });
        });
      }
      return viewableContentIdsSet;
    },
    activeContentTypes() {
      return Object.keys(this.contentColors).filter(type => !this.hiddenTypes.includes(type));
    }
  },
  methods: {
    ...mapActions('user', ['fetchUsersByProductVariant']),
    ...mapActions('content', ['fetchContentListByProductVariant', 'fetchUserContentViewCount','exportUsersContentActivityReport']),
    
    async fetchSubscribedUsersByProductVariant() {
      try {
        await this.fetchUsersByProductVariant(this.productVariantData.id);
        if (this.usersByProductVariant && this.usersByProductVariant[this.productVariantData.id]) {
          this.users = this.usersByProductVariant[this.productVariantData.id].slice().sort((a, b) => a.id - b.id);
          this.loadMoreUsers();
        }

      } catch (error) {
        console.error('Error fetching users:', error);
        this.error = 'Failed to fetch users';
      }
    },
    async fetchContentList() {
      try {
        const res = await this.fetchContentListByProductVariant({ id: this.productVariantData.id });
        if (res && Array.isArray(res)) {
          this.contents = res;
        } else {
          throw new Error('Invalid content data received');
        }
      } catch (error) {
        console.error('Error fetching content list:', error);
        this.error = 'Failed to fetch content list';
      }
    },
    async fetchContentViewData(userIds) {
      try {
        const res = await this.fetchUserContentViewCount({productVariantId: this.productVariantData.id, userIds});
        if (res && Array.isArray(res)) {

          const newContentViewData = [...res];

          newContentViewData.forEach(cv => {
            const userId = parseInt(cv.userID);
            if(!this.userContentViewMap.has(userId)) {
              this.userContentViewMap.set(userId, new Map());
            }

            const currentUserContentViewMap = this.userContentViewMap.get(userId);

            const contentId = parseInt(cv.contentID);

            if(!currentUserContentViewMap.has(contentId)) {
              currentUserContentViewMap.set(contentId, cv);
              this.userContentViewMap.set(userId, currentUserContentViewMap);
            }
          });

        } else {
          throw new Error('Invalid content view data received');
        }
      } catch (error) {
        console.error('Error fetching content view data:', error);
        this.error = 'Failed to fetch content view data';
      }
    },
    getViewedContentCountForUser(userId) {
      if(!this.userContentViewMap || !this.userContentViewMap.has(parseInt(userId))){
        return 0;
      }
      const userViewMap = this.userContentViewMap.get(parseInt(userId));
      let count = 0;
      userViewMap.forEach((viewData,contentId) => {
        if (this.visibleContentIds.has(contentId)) {
          count++;
        }
      });
      return count;
    },
    getContentViewCount(userId, contentId) {
      try {
        if(!this.userContentViewMap)
          return 0;

        const currentUserViewData = this.userContentViewMap.get(parseInt(userId));

        //console.log(currentUserViewData, userId);

        if(!currentUserViewData || currentUserViewData.size === 0) {
          return 0;
        }

        const currentContentViewData = currentUserViewData.get(parseInt(contentId));

        if(currentContentViewData) {
          return currentContentViewData.viewCount;
        }

        return 0;
      } catch (error) {
        console.error('Error getting content view count:', error);
        return 0;
      }
    },
    getTotalColspan(module) {
      if (!module || !module.chapters) return 0;
      return module.chapters.reduce((sum, chapter) => {
        if (!chapter || !chapter.contents) return sum;
        return sum + chapter.contents.length;
      }, 0);
    },
    toggleContentType(type) {
      const index = this.hiddenTypes.indexOf(type);
      if (index === -1) {
        this.hiddenTypes.push(type);
      } else {
        this.hiddenTypes.splice(index, 1);
      }
    },
    async toggleUserStatus(status) {
      try {
        this.loading = true;
        const index = this.activeUserStatuses.indexOf(status);
        if (index === -1) {
          this.activeUserStatuses.push(status);
        } else {
          this.activeUserStatuses.splice(index, 1);
        }
        await this.resetAndLoadUsers();
      }
      catch (err) {
        console.error(err);
      }
      finally {
        this.loading = false;
      }
    },
    async resetAndLoadUsers(){
      this.loadedUsers = [];
      this.userContentViewMap = new Map();
      await this.loadMoreUsers();
    },  
    async loadMoreUsers() {
      if (!this.isMoreUsersAvailableToLoad)
        return;
      try {
        this.isLoadingMore = true;
        const nextUsers = this.filteredUsers.slice(this.loadedUsers.length, this.loadedUsers.length + this.numOfUsersToLoad);
        const userIds = nextUsers.map(user => user.id).join(',');
        await this.fetchContentViewData(userIds);
        this.loadedUsers.push(...nextUsers);
      }
      catch (err) {
        console.error(err);
      }
      finally {
        this.isLoadingMore = false;
      }
    },
    truncateText(text, maxLength = 45) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    async handleUserSelection() {
      try {
        this.loading = true;
        this.loadedUsers = [];
        this.userContentViewMap = new Map();
        await this.loadMoreUsers();
      } catch (err) {
        console.error("Error in handleUserSelection:", err);
        this.error = "Failed to filter users.";
      } finally {
        this.loading = false;
      }
    },
    async checkSelectedUsersLength(){
    try{
      this.loading = true;
      if(this.selectedUsers.length === 0){
        await this.handleUserSelection();
      }
      return;
    }
    catch(err){
      console.error(err);
    }
    finally{
      this.loading = false;
    }
    },
    formatDateWithTime(datetime) {
			if(!datetime) {
				return "--"
			}

			let date = new Date(datetime);
			var hours = date.getHours();
			var minutes = date.getMinutes();
			var ampm = hours >= 12 ? "PM" : "AM";
			hours = hours % 12;
			hours = hours ? hours : 12; // the hour '0' should be '12'
			minutes = minutes < 10 ? "0" + minutes : minutes;
			var strTime = hours + ":" + minutes + " " + ampm;
			return toShortDateString(date) + "  " + strTime;
		},
    formatDate(dateString) {
      const date = new Date(dateString);
      const options = { year: 'numeric', month: 'short', day: '2-digit' };
      return date.toLocaleDateString(undefined, options);
    },
    daysBetween(start, end) {
      const s = new Date(start);
      const e = new Date(end);
      return Math.floor((e - s) / (1000 * 60 * 60 * 24));
    },
    generateUserInformationTooltip(userSubscription) {
      const enrolledAt = userSubscription.createdAt;
      const currentDateTime = new Date();
      let expiresAt = userSubscription.expiresAt;
      if(this.productVariantData?.endDate){
        if(!expiresAt || new Date(this.productVariantData.endDate) < new Date(expiresAt)){
          expiresAt = this.productVariantData.endDate;
        }
      }
      let tooltip = `<div style='text-align: left;'>${userSubscription.enrollmentStatus}<br>`;
      if (userSubscription.enrollmentStatus.toLowerCase() === 'allowed') {
      const daysSince = this.daysBetween(enrolledAt, currentDateTime);
      tooltip += `Access since: ${daysSince} days<br>`;
      if (expiresAt) {
        const daysLeft = this.daysBetween(currentDateTime, expiresAt);
        if (daysLeft < 0) {
          tooltip += `Access already expired`;
        } else {
          tooltip += `Access will expire after: ${daysLeft} days`;
        }
      } else {
        tooltip += `Access for lifetime`;
      }
    }
      tooltip += `</div>`;
      return tooltip;
    },
    async exportUsersContentReport() {
  try{
    this.exportLoading = true;
    const userContentAvtivityReportExportData = {
      productVariantID: parseInt(this.productVariantData.id),
      selectedUsers: this.selectedUsers || [],
      selectedContentTypes: (this.activeContentTypes || []).map(type => type.toLowerCase()),
      selectedEnrollmentStatuses: this.activeUserStatuses || [],
    };
    const result = await this.exportUsersContentActivityReport(userContentAvtivityReportExportData);
    if (result && result.url) {
      const link = document.createElement('a');
      link.href = result.url;
      link.setAttribute('download',result.name?result.name:'UsersContentActivityReport.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(result); 
    }    
}catch(err){
    this.$toasted.error(
      `${err}`,
      {
        icon: "exclamation-triangle",
        theme: "outline",
        duration: 3000,
        position: "bottom-center",
        singleton: true,
      }
    );
  }finally{
    this.exportLoading = false;
  }
}
  },
  async created() {
    try {
      this.loading = true;
      await Promise.all([
        this.fetchSubscribedUsersByProductVariant(),
        this.fetchContentList(),
        // this.fetchContentViewData()
      ]);
    } catch (error) {
      console.error('Error initializing component:', error);
      this.error = 'Failed to load data';
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.pivot-table-container {
  margin: 20px;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}
.filters-container {
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 3px 6px #d5d5d529;
  background-color: #ffffff;
  margin-bottom: 1.5rem;
}

.table-wrapper {
  position: relative;
  flex: 1;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

.fixed-column {
  position: sticky;
  left: 0;
  z-index: 1;
  background: white;
  min-width: 200px;
}

thead .fixed-column {
  z-index: 3;
}

th, td {
  border: 1px solid #ddd;
  text-align: left;
  min-width: 150px;
  background: white;
}

td {
  text-align: center;
}

.module-header {
  text-align: start;
  background-color: #e6e6e6 !important;
  font-weight: bold;
  padding: 15px;
  text-transform: uppercase;
}

.chapter-header {
  text-align: start;
  background-color: #f2f2f2 !important;
  font-weight: bold;
  padding: 12px;
}

.content-header th {
  background-color: #f8f8f8 !important;
  text-align: center;
}

h2 {
  text-align: center;
  margin-bottom: 20px;
  font-size: 24px;
  color: #333;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  color: #666;
}

.error {
  color: #dc3545;
  text-align: center;
  margin-top: 10px;
  padding: 10px;
  background-color: #fff;
  border-radius: 4px;
}

.table-wrapper::after {
  content: '';
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(to right, rgba(0,0,0,0.1), transparent);
  pointer-events: none;
}

.table-wrapper::before {
  content: '';
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 5px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.1), transparent);
  pointer-events: none;
}

tbody tr:hover {
  background-color: #f5f5f5;
}

tbody tr:hover td {
  background-color: #f5f5f5;
}

tbody tr:hover td.fixed-column {
  background-color: #f5f5f5;
}

@media (max-width: 1024px) {
  .fixed-column {
    min-width: 150px;
  }

  th, td {
    font-size: 14px;
  }
}

.loader-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999;
  color: var(--primary-color);
}

.user-email-phone {
  font-size: smaller;
  margin-bottom: 0.5rem;
}

.content-icon {
  margin-right: 5px;
  color: var(--primary-color); 
}

.no-records {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #888;
  margin-top: 20px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 5px;
  border-radius: 3px;
  transition: opacity 0.3s;
  font-weight: bold;
}

.legend-item:hover {
  background-color: #f0f0f0;
}

.legend-item-inactive {
  opacity: 0.5;
}

.legend-color {
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 3px;
}

.status-dot {
  width: 6px;
  height: 6px;
  display: inline-block;
  border-radius: 50%;
}

.unenrolled-dot {
  background-color: red;
}

.allowed-dot {
  background-color: green;
}

.blocked-dot {
  background-color: yellow;
}
.cross-tick-icon{
  font-size: x-large;
}
.filter-user-type-btn {
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 0.625rem 1rem;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.125rem;
  letter-spacing: 0.05em;
  color: #000000;
  text-decoration: none;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.filter-user-type-btn.active {
  border: 1px solid var(--primary-color);
}
.filter-btn {
	background: #ffffff;
	border: 1px solid var(--primary-color);
	box-sizing: border-box;
	border-radius: 8px;
	padding: 0.5rem 2rem;
	text-decoration: none;
	color: #000000;
	display: flex;
	-moz-column-gap: 0.5rem;
	column-gap: 0.5rem;
	align-items: center;
	transition: all 0.3s ease;
}

.filter-btn:hover {
	box-shadow: (3px 5px 6px rgba(197, 197, 197, 0.3));
}
.user-selection:nth-child(1){
  width: 85vw;
}
</style>